import {
  checkIsSectionFeedbackWithCurrentGrade,
  sectionFeedbackCorrectValueToDisplayValue,
  type FeedbackWithCurrentGrade,
  type TestGradingResult,
} from "@repo/app-types/entities/recordingAssessmentFeedback";
import {
  getNormalizedNodeGradingComparableValue,
  getNormalizedNodeGradingDisplayValue,
} from "@repo/app-types/helpers/normalizedNodeGradingHelpers";
import { sectionIsNotApplicableValueToDisplayValue } from "@repo/app-types/helpers/recordingAssessmentFeedbackHelpers";
import { capitalize } from "@repo/ui/helpers/helpers";
import type { FC } from "react";
import { appPaths } from "../../../app/appPaths.js";
import { evaluateFeedbackCorrectness } from "../feedbackAccuracyHelpers.js";
import { FeedbackCell } from "./feedbackCell.js";

interface GraderFeedbackTableProps {
  feedbacks: readonly FeedbackWithCurrentGrade[];
  onFeedbackClick: (feedback: FeedbackWithCurrentGrade) => void;
  testGradingResults?: TestGradingResult[] | null;
}

export const GraderFeedbackTable: FC<GraderFeedbackTableProps> = ({
  feedbacks,
  testGradingResults,
  onFeedbackClick,
}) => {
  const formatValueForDisplay = (
    f: FeedbackWithCurrentGrade,
    value: string,
  ) => {
    if (checkIsSectionFeedbackWithCurrentGrade(f)) {
      return sectionFeedbackCorrectValueToDisplayValue(value) ?? "-";
    }
    return capitalize(value);
  };

  return (
    <div className="overflow-hidden rounded-lg border border-border-light">
      <table className="min-w-full divide-y divide-border-light">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
              Date
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
              Submitted by
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
              Current grade
            </th>
            {testGradingResults && (
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
                Test grade
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">
              Correct grade
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light bg-white">
          {feedbacks.map((f) => {
            const testResult = testGradingResults?.find(
              (r) => r.feedbackId === f.id,
            );

            const isSection = checkIsSectionFeedbackWithCurrentGrade(f);
            const gradingValueType = isSection
              ? "section"
              : f.data.criteria_node.grading_value_type;
            const testDisplayValue = testResult
              ? testResult.testStatus === "error"
                ? "Error"
                : getNormalizedNodeGradingDisplayValue({
                    gradingValueType,
                    grading: {
                      status: testResult.testStatus,
                      value: testResult.testValue,
                    },
                  })
              : null;
            const comparableTestValue = testResult
              ? testResult.testStatus === "error"
                ? undefined
                : getNormalizedNodeGradingComparableValue({
                    gradingValueType,
                    grading: {
                      status: testResult.testStatus,
                      value: testResult.testValue,
                    },
                  })
              : undefined;

            return (
              <tr
                key={f.id}
                className={"cursor-pointer transition-colors hover:bg-gray-50"}
                onClick={() => {
                  if (isSection) {
                    window.open(
                      appPaths.playbookCallAssessment.build({
                        playbookId: f.recording_assessment_template_id,
                        gradingId: f.grading_id,
                      }),
                      "_blank",
                      "noopener,noreferrer",
                    );
                  } else {
                    onFeedbackClick(f);
                  }
                }}
              >
                <td className="px-4 py-4 text-sm text-gray-600">
                  {new Date(f.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </td>

                <td className="px-4 py-4 text-sm text-gray-600">
                  {f.feedback_author_name ?? "-"}
                </td>

                <td className="px-4 py-4 text-sm">
                  <FeedbackCell
                    value={
                      isSection
                        ? sectionIsNotApplicableValueToDisplayValue(
                            f.current_grade.is_not_applicable,
                          )
                        : formatValueForDisplay(f, f.current_grade.value)
                    }
                    commentary={f.current_grade.commentary}
                    correctness={evaluateFeedbackCorrectness(
                      f,
                      isSection
                        ? f.current_grade.is_not_applicable
                          ? "n/a"
                          : "a"
                        : f.current_grade.value,
                    )}
                  />
                </td>

                {testGradingResults && (
                  <td className="px-4 py-4 text-sm">
                    {testResult ? (
                      <FeedbackCell
                        value={testDisplayValue ?? "-"}
                        commentary={testResult.testCommentary ?? undefined}
                        correctness={evaluateFeedbackCorrectness(
                          f,
                          comparableTestValue,
                        )}
                      />
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                )}

                <td className="px-4 py-4 text-sm">
                  <FeedbackCell
                    value={formatValueForDisplay(f, f.data.correct_value)}
                    commentary={f.data.explanation}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
